import { Check } from "lucide-react";
import type { MailProviderPreset } from "@/lib/types/mail";
import { cn } from "@/lib/cn";
import { ProviderBrandIcon } from "./icons/ProviderBrandIcon";

type Props = {
  provider: MailProviderPreset;
  selected: boolean;
  onSelect: () => void;
  /** Pleine largeur, icône à gauche — Google / Microsoft / Autre IMAP */
  layout?: "tile" | "row";
};

export function ProviderCard({ provider, selected, onSelect, layout = "tile" }: Props) {
  const isManual = provider.id === "manual";
  const isRow = layout === "row";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative flex w-full rounded-xl border text-center transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--mail-surface)]",
        isRow
          ? "flex-row items-center gap-3 px-3 py-2.5 text-left"
          : "flex-col items-center gap-2 px-2 py-3",
        selected
          ? "border-[var(--mail-accent)] bg-[var(--mail-accent-soft)] shadow-[var(--mail-shadow-md)] ring-1 ring-[var(--mail-accent)]/25"
          : "border-[var(--mail-border-soft)] bg-[var(--mail-surface)] hover:border-[var(--mail-accent)]/30 hover:shadow-[var(--mail-shadow-sm)]",
        isManual && !selected && "border-dashed"
      )}
    >
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-xl border bg-[var(--mail-surface-elevated)] shadow-sm",
          isRow ? "h-9 w-9" : "h-10 w-10",
          selected ? "border-[var(--mail-accent)]/25" : "border-[var(--mail-border-soft)] group-hover:border-[var(--mail-accent)]/20"
        )}
      >
        <ProviderBrandIcon
          providerId={provider.id}
          className={cn("h-5 w-5", isManual && "h-4 w-4 text-[var(--mail-muted)]")}
        />
      </span>

      <span className={cn("min-w-0", isRow ? "flex-1 text-left" : "w-full px-0.5")}>
        <span className="block truncate text-xs font-semibold text-[var(--mail-text)]">
          {provider.label}
        </span>
        {isRow && provider.hint ? (
          <span className="mt-0.5 block truncate text-[10px] text-[var(--mail-muted)]">
            {provider.hint}
          </span>
        ) : null}
      </span>

      <span
        className={cn(
          "absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full border transition-colors",
          selected
            ? "border-[var(--mail-accent)] bg-[var(--mail-accent)] text-white"
            : "border-transparent bg-transparent text-transparent"
        )}
        aria-hidden
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </span>
    </button>
  );
}
